import { useState, useEffect } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Area } from "recharts";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Gamepad, CheckCircle, Target } from "lucide-react";

interface GamePerformanceData {
  round: number;
  percentage: number;
}

interface GameStats {
  gamesPlayed: number;
  convergenceRate: number;
  averageRounds: number;
}

export const GamePerformanceChart = () => {
  const [performanceData, setPerformanceData] = useState<GamePerformanceData[]>([]);
  const [gameStats, setGameStats] = useState<GameStats>({
    gamesPlayed: 0,
    convergenceRate: 0,
    averageRounds: 0
  });
  const [loading, setLoading] = useState(true);
  const {
    user
  } = useAuth();
  useEffect(() => {
    const fetchPerformanceData = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);

        // First, get all games for this user (completed or not)
        const {
          data: allGamesData,
          error: allGamesError
        } = await supabase.from('games').select('id, status').eq('user_id', user.id);
        if (allGamesError) throw allGamesError;

        // Calculate total games played
        const totalGamesPlayed = allGamesData?.length || 0;

        // Get completed games only
        const completedGames = allGamesData?.filter(game => game.status === 'completed') || [];
        const totalCompletedGames = completedGames.length;

        // Calculate convergence rate
        const convergenceRate = totalGamesPlayed > 0 ? totalCompletedGames / totalGamesPlayed * 100 : 0;

        // Get all rounds data for completed games to calculate average rounds
        let averageRounds = 0;
        if (totalCompletedGames > 0) {
          const gameIds = completedGames.map(game => game.id);

          // Get round counts for each completed game
          const roundPromises = gameIds.map(gameId => supabase.from('rounds').select('*', {
            count: 'exact'
          }).eq('game_id', gameId));
          const roundResults = await Promise.all(roundPromises);

          // Calculate sum of rounds across all completed games
          const totalRounds = roundResults.reduce((sum, result) => {
            return sum + (result.count || 0);
          }, 0);

          // Calculate average rounds per completed game
          averageRounds = totalRounds / totalCompletedGames;
        }

        // Set the game stats
        setGameStats({
          gamesPlayed: totalGamesPlayed,
          convergenceRate: Math.round(convergenceRate),
          averageRounds: Math.round(averageRounds)
        });

        // If there are no completed games, there's no performance data to show
        if (totalCompletedGames === 0) {
          setLoading(false);
          return;
        }

        // Get all rounds data for completed games
        const gameIds = completedGames.map(game => game.id);
        const {
          data: roundsData,
          error: roundsError
        } = await supabase.from('rounds').select('game_id, round_number, user_word, ai_word').in('game_id', gameIds).order('round_number', {
          ascending: true
        });
        if (roundsError) throw roundsError;
        if (!roundsData || roundsData.length === 0) {
          setLoading(false);
          return;
        }

        // Group rounds by game_id to find convergence round for each game
        const gameRounds: Record<string, any[]> = {};
        roundsData.forEach(round => {
          if (!gameRounds[round.game_id]) {
            gameRounds[round.game_id] = [];
          }
          gameRounds[round.game_id].push(round);
        });

        // Find the convergence round for each game
        const convergenceRounds: number[] = [];
        Object.values(gameRounds).forEach(rounds => {
          for (const round of rounds) {
            if (round.user_word && round.ai_word && round.user_word.toLowerCase() === round.ai_word.toLowerCase()) {
              convergenceRounds.push(round.round_number);
              break;
            }
          }
        });

        // Create data for the chart (rounds 1-20, not 1-30)
        const data: GamePerformanceData[] = [];
        for (let round = 1; round <= 20; round++) {
          // Count games that reached convergence by this round
          const convergedByThisRound = convergenceRounds.filter(r => r <= round).length;
          const percentage = convergedByThisRound / totalCompletedGames * 100;
          data.push({
            round,
            percentage: Math.round(percentage)
          });
        }
        setPerformanceData(data);
      } catch (error) {
        console.error("Error fetching game performance data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPerformanceData();
  }, [user]);

  // Hide the entire component if there's no user
  if (!user) {
    return null;
  }
  if (loading) {
    return <div className="h-64 flex items-center justify-center">
        <p className="text-muted-foreground">Loading performance data...</p>
      </div>;
  }
  return <div className="space-y-8">
      {/* Stats Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Games Played</p>
                <h3 className="text-3xl font-bold">{gameStats.gamesPlayed}</h3>
              </div>
              
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Convergence Rate</p>
                <h3 className="text-3xl font-bold">{gameStats.convergenceRate}%</h3>
              </div>
              
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Avg. Rounds</p>
                <h3 className="text-3xl font-bold">{gameStats.averageRounds}</h3>
              </div>
              
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Chart Card */}
      {performanceData.length > 0 && <Card>
          <CardContent className="pt-24 pb-16 py-0">
            <p className="text-sm text-muted-foreground mb-4 my-[24px]">
              Percentage of games converged by each round
            </p>
            <div className="h-64 w-full mt-2">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={performanceData} margin={{
              top: 10,
              right: 10,
              left: 0,
              bottom: 20
            }}>
                  <XAxis dataKey="round" tick={{
                fontSize: 10
              }} />
                  <YAxis tickFormatter={value => `${value}%`} tick={{
                fontSize: 10
              }} width={35} axisLine={true} tickLine={true} />
                  <Tooltip formatter={value => [`${value}%`, 'Percentage']} labelFormatter={label => `Round ${label}`} />
                  <Area type="monotone" dataKey="percentage" fill="rgba(142, 145, 150, 0.2)" stroke="none" />
                  <Line type="monotone" dataKey="percentage" stroke="#6b7280" strokeWidth={2} dot={{
                r: 3
              }} activeDot={{
                r: 5
              }} animationDuration={1000} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>}
    </div>;
};
